import { useEffect } from "react";
import { useParams, useNavigate, useSearchParams, Link, useLocation } from "react-router-dom";
import { SEO } from "@/components/seo/SEO";
import { generateBreadcrumbSchema, generateLearningResourceSchema } from "@/lib/seoSchemas";
import { TopicHub } from "@/components/navigation/TopicHub";
import { UnifiedContentViewer } from "@/components/navigation/UnifiedContentViewer";
import { useContinueLearning } from "@/hooks/useContinueLearning";
import { Button } from "@/components/ui/button";
import { BookOpen, Brain, Bookmark, Sparkles } from "lucide-react";

const Study = () => {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialMode = (searchParams.get('mode') as 'mcq' | 'notes' | 'mixed') || 'mixed';
  const { updateLearningState } = useContinueLearning();

  // Update learning state when viewing Study Hub
  useEffect(() => {
    if (!topicId) {
      updateLearningState({
        path: '/study',
        type: 'study',
        title: 'Study Hub',
      });
    }
  }, [topicId, updateLearningState]);
  
  // Handle topic selection from hub
  const handleTopicSelect = (selectedTopicId: string, contentType: 'mcq' | 'notes' | 'mixed') => {
    if (contentType === 'mcq') {
      navigate(`/practice?topic=${selectedTopicId}`);
    } else if (contentType === 'notes') {
      navigate(`/notes?topic=${selectedTopicId}`);
    } else {
      navigate(`/study/${selectedTopicId}?mode=${contentType}`);
    }
  };

  // Handle topic change (going back to hub or changing topics)
  const handleTopicChange = (newTopicId: string) => {
    if (newTopicId === 'hub') {
      navigate('/study');
    } else {
      navigate(`/study/${newTopicId}`);
    }
  };

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  
  const schemas = [
    generateBreadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Study", path: "/study" }
    ], origin),
    generateLearningResourceSchema(
      "Medical Study Hub",
      "Integrated study platform combining medical notes and practice questions. Choose topics and study modes for comprehensive exam preparation.",
      `${origin}/study`
    )
  ];

  return (
    <div className="space-y-6 pb-24 animate-fade-in">
      <SEO
        title={topicId ? "Medical Study - Topic Review with MCQs and Notes" : "Medical Study Hub - Integrated Learning Platform"}
        description={topicId ? "Study medical content with integrated MCQs and detailed notes. Practice and learn simultaneously for better retention." : "Choose medical topics for focused study sessions. Access integrated MCQs and notes for comprehensive exam preparation."}
        path={topicId ? `/study/${topicId}` : "/study"}
        keywords="medical study platform, integrated medical learning, medical notes and mcqs, medical topic review, comprehensive medical study"
        schema={schemas}
      />

      {!topicId && <StudySubNav />}

      {topicId ? (
        <UnifiedContentViewer
          topicId={topicId}
          initialMode={initialMode}
          onTopicChange={handleTopicChange}
        />
      ) : (
        <TopicHub onTopicSelect={handleTopicSelect} />
      )}
    </div>
  );
};

const StudySubNav = () => {
  const location = useLocation();
  const items = [
    { to: "/study", label: "Topics", icon: BookOpen },
    { to: "/flashcards", label: "Flashcards", icon: Brain },
    { to: "/bookmarks", label: "Bookmarks", icon: Bookmark },
    { to: "/quiz-builder", label: "Quiz Builder", icon: Sparkles },
  ];
  return (
    <nav className="flex flex-wrap gap-2 border-b pb-3" aria-label="Study sections">
      {items.map(({ to, label, icon: Icon }) => {
        const active = location.pathname === to;
        return (
          <Button key={to} asChild variant={active ? "default" : "ghost"} size="sm">
            <Link to={to}>
              <Icon className="mr-2 h-4 w-4" />
              {label}
            </Link>
          </Button>
        );
      })}
    </nav>
  );
};

export default Study;